import { Link } from 'react-router-dom';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { ExitToAppRounded as ExitToAppIcon } from '@material-ui/icons';

import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { ADMIN_DELIVERY_CITIES_APP_PATHS } from '../../constants/delivery-cities-app-paths.constants';
import { DeliveryCity, DeliveryCitySortColumn, DeliveryCitySortDirection } from '../../types/delivery-city.type';

interface DeliveryCitiesTableHeader {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align?: 'left' | 'right' | 'center';
  sortColumn?: DeliveryCitySortColumn;
}

interface DeliveryCitiesTableProps {
  deliveryCities: DeliveryCity[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  sortBy: DeliveryCitySortColumn;
  sortDirection: DeliveryCitySortDirection;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSortChange: (column: DeliveryCitySortColumn) => void;
}

const TABLE_HEADERS: DeliveryCitiesTableHeader[] = [
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 60 },
  { key: 'name', label: 'Ville', sortColumn: 'name', minWidth: 160 },
  { key: 'zipCode', label: 'Code postal', sortColumn: 'zipCode', minWidth: 120 },
  { key: 'zone', label: 'Zone', minWidth: 160 },
  { key: 'slots', label: 'Créneaux', align: 'center', minWidth: 100 },
  { key: 'status', label: 'Statut', minWidth: 100 },
];

const COLUMN_COUNT = TABLE_HEADERS.length;

function DeliveryCitiesTableHeaderCell({
  header,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  header: DeliveryCitiesTableHeader;
  sortBy: DeliveryCitySortColumn;
  sortDirection: DeliveryCitySortDirection;
  onSortChange: (column: DeliveryCitySortColumn) => void;
}) {
  const { sortColumn } = header;
  const isSorted = sortColumn === sortBy;

  return (
    <TableCell
      aria-label={header.ariaLabel}
      align={header.align ?? 'left'}
      sortDirection={isSorted ? sortDirection : false}
      style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
    >
      {sortColumn ? (
        <TableSortLabel
          active={isSorted}
          direction={isSorted ? sortDirection : 'asc'}
          onClick={() => onSortChange(sortColumn)}
        >
          {header.label}
        </TableSortLabel>
      ) : (
        header.label
      )}
    </TableCell>
  );
}

function DeliveryCityTableRow({ deliveryCity }: { deliveryCity: DeliveryCity }) {
  return (
    <TableRow hover tabIndex={-1}>
      <TableCell align="left">
        <IconButton
          size="small"
          aria-label="voir"
          component={Link}
          to={ADMIN_DELIVERY_CITIES_APP_PATHS.DELIVERY_CITY_DETAILS_PATH(deliveryCity.id)}
        >
          <ExitToAppIcon />
        </IconButton>
      </TableCell>
      <TableCell align="left">{deliveryCity.name}</TableCell>
      <TableCell align="left">{deliveryCity.zipCode}</TableCell>
      <TableCell align="left">{deliveryCity.zone.name}</TableCell>
      <TableCell align="center">{deliveryCity.slots.length}</TableCell>
      <TableCell align="left">
        <StatusChip isActive={deliveryCity.isActive} archived={deliveryCity.archivedAt !== null} />
      </TableCell>
    </TableRow>
  );
}

export function DeliveryCitiesTable({
  deliveryCities,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  sortBy,
  sortDirection,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: DeliveryCitiesTableProps) {
  return (
    <Paper>
      <TableContainer style={{ maxHeight: '75svh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <DeliveryCitiesTableHeaderCell
                  key={header.key}
                  header={header}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSortChange={onSortChange}
                />
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={COLUMN_COUNT} />}
          {!isLoading && (
            <TableBody>
              {deliveryCities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} style={{ textAlign: 'center' }}>
                    Aucune ville de livraison trouvée
                  </TableCell>
                </TableRow>
              )}
              {deliveryCities.map((deliveryCity) => (
                <DeliveryCityTableRow key={deliveryCity.id} deliveryCity={deliveryCity} />
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}
